import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Spell } from '../../types/interfaces';
import { useTranslation } from '../../i18n/useTranslation';
import {
  LOADOUT_SLOTS as SLOTS,
  type LoadoutSlot as Slot,
  loadLoadout,
  saveLoadout,
  defaultLoadout,
} from '../../systems/loadout';
import styles from './CommandBar.module.css';

// ─────────────────────────────────────────────────────────────────────────────
// CommandBar — barra de comando ÚNICA e customizável da batalha.
// Cada slot pode conter um FEITIÇO, um ITEM (consumível) ou ficar VAZIO.
// O jogador monta a barra pelo botão ⚙ (config). Se um item acaba, o slot
// fica vazio automaticamente. A escolha é lembrada por classe (localStorage).
// Atalhos de teclado 1..8 acionam os slots (feitiço → seleciona, item → usa).
// ─────────────────────────────────────────────────────────────────────────────

export interface ConsumableEntry {
  id: string;
  name: string;
  icon_url?: string;
  quantity: number;
}

interface CommandBarProps {
  token: string;
  /** Chave de persistência do loadout (ex.: `combat.loadout.<classe>`). */
  loadoutKey: string;
  spells: Spell[];
  selectedSpellId: string | null;
  currentAp: number;
  cooldowns: Record<string, number>;
  /** É o turno do jogador. */
  active: boolean;
  /** Muda a cada COMBAT_STATE — dispara re-fetch das quantidades de item. */
  refreshNonce: number;
  onSelectSpell: (spell: Spell) => void;
  onUseItem: (itemId: string) => void;
}

export function CommandBar({
  token,
  loadoutKey,
  spells,
  selectedSpellId,
  currentAp,
  cooldowns,
  active,
  refreshNonce,
  onSelectSpell,
  onUseItem,
}: CommandBarProps) {
  const { t } = useTranslation();
  const [consumables, setConsumables] = useState<ConsumableEntry[]>([]);
  const [slots, setSlots] = useState<Slot[]>(() => loadLoadout(loadoutKey));
  const [configOpen, setConfigOpen] = useState(false);
  const [failedImg, setFailedImg] = useState<Set<string>>(new Set());
  const [initialised, setInitialised] = useState(false);

  const spellById = useMemo(() => new Map(spells.map(s => [s.id, s])), [spells]);
  const itemById  = useMemo(() => new Map(consumables.map(c => [c.id, c])), [consumables]);

  // ── Inventário: carrega e recarrega a cada COMBAT_STATE (item pode ter sido
  //    usado nesta jogada — quantidade precisa refletir na hora). ────────────
  const fetchConsumables = useCallback(async () => {
    try {
      const res = await fetch('/api/user/inventory', {
        headers: { Authorization: `Bearer ${token}` },
        cache: 'no-store',
      });
      if (!res.ok) return;
      const json = await res.json();
      const list: ConsumableEntry[] = (json.inventory ?? [])
        .filter((e: any) => e.item?.item_type === 'consumable' || e.item?.slot === 'Consumable')
        .map((e: any) => ({
          id: e.item.id,
          name: e.item.name,
          icon_url: e.item.icon_url,
          quantity: e.quantity ?? 0,
        }));
      setConsumables(list);
    } catch { /* ignore */ }
  }, [token]);

  useEffect(() => { fetchConsumables(); }, [fetchConsumables, refreshNonce]);

  // ── Loadout padrão na 1ª vez (sem nada salvo): feitiços e depois itens. ────
  useEffect(() => {
    if (initialised) return;
    const hasSaved = slots.some(s => s !== null);
    if (!hasSaved && (spells.length > 0 || consumables.length > 0)) {
      const next = defaultLoadout(spells.map(s => s.id), consumables.map(c => c.id));
      setSlots(next);
      saveLoadout(loadoutKey, next);
    }
    if (spells.length > 0) setInitialised(true);
  }, [spells, consumables, slots, initialised, loadoutKey]);

  const commit = (next: Slot[]) => { setSlots(next); saveLoadout(loadoutKey, next); };

  const removeSlot = (idx: number) => {
    const next = slots.slice();
    next[idx] = null;
    commit(next);
  };

  const toggleInBar = (slot: NonNullable<Slot>) => {
    const existing = slots.findIndex(s => s && s.kind === slot.kind && s.id === slot.id);
    if (existing >= 0) { removeSlot(existing); return; }
    const empty = slots.findIndex(s => s === null);
    if (empty < 0) return; // barra cheia
    const next = slots.slice();
    next[empty] = slot;
    commit(next);
  };

  const resetDefault = () => {
    commit(defaultLoadout(spells.map(s => s.id), consumables.map(c => c.id)));
  };

  // ── Estado de um slot para render / atalho ───────────────────────────────
  type SlotView =
    | { empty: true }
    | { empty: false; kind: 'spell'; spell: Spell; disabled: boolean; onCd: boolean; selected: boolean }
    | { empty: false; kind: 'item'; item: ConsumableEntry; disabled: boolean };

  const slotState = (slot: Slot): SlotView => {
    if (!slot) return { empty: true };
    if (slot.kind === 'spell') {
      const spell = spellById.get(slot.id);
      if (!spell) return { empty: true };
      const onCd = (cooldowns[spell.id] ?? 0) > 0;
      const noAp = spell.ap_cost > currentAp;
      return {
        empty: false, kind: 'spell', spell,
        disabled: !active || onCd || noAp,
        onCd, selected: spell.id === selectedSpellId,
      };
    }
    const item = itemById.get(slot.id);
    // Item acabou → slot fica VAZIO (mas guardado no loadout p/ quando repor).
    if (!item || item.quantity <= 0) return { empty: true };
    return { empty: false, kind: 'item', item, disabled: !active };
  };

  const activateSlot = useCallback((idx: number) => {
    const st = slotState(slots[idx]);
    if (st.empty || st.disabled) return;
    if (st.kind === 'spell') onSelectSpell(st.spell);
    else onUseItem(st.item.id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slots, consumables, spells, cooldowns, currentAp, active, selectedSpellId]);

  // ── Atalhos 1..8 ─────────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (configOpen) return;
      const n = parseInt(e.key, 10);
      if (Number.isNaN(n) || n < 1 || n > SLOTS) return;
      activateSlot(n - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [activateSlot, configOpen]);

  const imgOr = (id: string, url: string | null | undefined, fallback: string) =>
    url && !failedImg.has(id) ? (
      <img src={url} alt="" className={styles.icon} onError={() => setFailedImg(p => new Set(p).add(id))} />
    ) : (
      <span className={styles.letter}>{fallback.charAt(0).toUpperCase()}</span>
    );

  return (
    <div className={styles.wrap}>
      <button
        className={styles.configBtn}
        onClick={() => setConfigOpen(true)}
        title={t('command.configure' as any)}
      >⚙</button>

      <div className={styles.bar}>
        {slots.map((slot, idx) => {
          const st = slotState(slot);
          if (st.empty) {
            return <div key={idx} className={styles.slotEmpty}><span className={styles.slotNum}>{idx + 1}</span></div>;
          }
          if (st.kind === 'spell') {
            const nm = t(`spell.${st.spell.id}.name` as any) || st.spell.name;
            return (
              <button
                key={idx}
                className={styles.slot}
                data-sel={st.selected ? 'true' : 'false'}
                data-off={st.disabled ? 'true' : 'false'}
                disabled={st.disabled}
                onClick={() => activateSlot(idx)}
                title={`${nm} (${st.spell.ap_cost} AP)`}
              >
                <div className={styles.iconWrap}>{imgOr(st.spell.id, st.spell.icon_url, nm)}</div>
                <span className={styles.cost}>{st.spell.ap_cost}</span>
                <span className={styles.slotNum}>{idx + 1}</span>
                {st.onCd && <div className={styles.cd}>{cooldowns[st.spell.id]}</div>}
              </button>
            );
          }
          return (
            <button
              key={idx}
              className={`${styles.slot} ${styles.itemSlot}`}
              data-off={st.disabled ? 'true' : 'false'}
              disabled={st.disabled}
              onClick={() => activateSlot(idx)}
              title={`${st.item.name} — ${t('command.useItem' as any)} (${t('command.costsTurn' as any)})`}
            >
              <div className={styles.iconWrap}>{imgOr(st.item.id, st.item.icon_url, st.item.name)}</div>
              <span className={styles.qty}>×{st.item.quantity}</span>
              <span className={styles.slotNum}>{idx + 1}</span>
            </button>
          );
        })}
      </div>

      {configOpen && (
        <div className={styles.overlay} onClick={() => setConfigOpen(false)}>
          <div className={styles.modal} onClick={e => e.stopPropagation()}>
            <header className={styles.modalHead}>
              <h3>{t('command.configTitle' as any)}</h3>
              <button className={styles.closeBtn} onClick={() => setConfigOpen(false)}>✕</button>
            </header>

            <p className={styles.hint}>{t('command.configHint' as any)}</p>

            {/* Slots atuais */}
            <div className={styles.slotRow}>
              {slots.map((slot, idx) => {
                const label = slot
                  ? (slot.kind === 'spell'
                      ? (t(`spell.${slot.id}.name` as any) || spellById.get(slot.id)?.name || '?')
                      : (itemById.get(slot.id)?.name || '?'))
                  : '';
                return (
                  <button
                    key={idx}
                    className={`${styles.cfgSlot} ${slot ? '' : styles.cfgSlotEmpty}`}
                    onClick={() => slot && removeSlot(idx)}
                    title={slot ? t('command.removeSlot' as any) : ''}
                  >
                    <span className={styles.slotNum}>{idx + 1}</span>
                    <span className={styles.cfgSlotLabel}>{label || '—'}</span>
                  </button>
                );
              })}
            </div>

            <div className={styles.paletteGroup}>
              <span className={styles.paletteTitle}>{t('command.spells' as any)}</span>
              <div className={styles.palette}>
                {spells.map(s => {
                  const inBar = slots.some(sl => sl && sl.kind === 'spell' && sl.id === s.id);
                  const nm = t(`spell.${s.id}.name` as any) || s.name;
                  return (
                    <button key={s.id} className={`${styles.chip} ${inBar ? styles.chipOn : ''}`}
                      onClick={() => toggleInBar({ kind: 'spell', id: s.id })}>
                      {nm} <small>{s.ap_cost} AP</small>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className={styles.paletteGroup}>
              <span className={styles.paletteTitle}>{t('command.items' as any)}</span>
              <div className={styles.palette}>
                {consumables.length === 0 && <span className={styles.noItems}>{t('command.noItems' as any)}</span>}
                {consumables.map(c => {
                  const inBar = slots.some(sl => sl && sl.kind === 'item' && sl.id === c.id);
                  return (
                    <button key={c.id} className={`${styles.chip} ${inBar ? styles.chipOn : ''}`}
                      onClick={() => toggleInBar({ kind: 'item', id: c.id })}>
                      {c.name} <small>×{c.quantity}</small>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className={styles.modalActions}>
              <button className={styles.resetBtn} onClick={resetDefault}>{t('command.reset' as any)}</button>
              <button className={styles.doneBtn} onClick={() => setConfigOpen(false)}>{t('common.close')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
